import { useCallback, useReducer, useState } from 'react'

import { faArrowRotateLeft, faGrid2 } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { useBoolean } from 'usehooks-ts'

import {
  ButtonSizes,
  Checkbox,
  FormattedMessage,
  Popover,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Shadows, Typography } from '@b-stock/fe-core/design-system'

import { useManifestTableTracking } from '../../../hooks/useManifestTableTracking'
import { useManifestContext } from '../ManifestContext'
import { ControlButton, ControlButtonLabel } from '../ui'

const Panel = styled.div`
  ${Shadows.Popovers}
  background-color: ${Colors.Semantic.Neutral.white};
  border: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.25rem;
  display: flex;
  flex-direction: column;
  margin-top: 0.25rem;
  max-height: min(24rem, 60vh);
  max-width: calc(100vw - 2rem);
  min-width: 14rem;
`

const Heading = styled.div`
  align-items: center;
  border-bottom: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  display: flex;
  justify-content: space-between;
`

const Title = styled.div`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.dark_grey};
  font-weight: 700;
  letter-spacing: 0.025rem;
  padding: 0.5rem 0.75rem;
  text-transform: uppercase;
`

const ResetButton = styled.button`
  background: none;
  border: none;
  border-radius: 50%;
  color: ${Colors.Semantic.Neutral.dark_grey};
  cursor: pointer;
  margin-right: 0.25rem;
  padding: 0.5rem;

  &:hover {
    background: ${Colors.Semantic.Neutral.light_grey};
    color: ${Colors.Semantic.Neutral.very_dark_grey};
  }
`

const Group = styled.div`
  overflow-y: auto;
  padding: 0.25rem 0;
`

const Item = styled.label`
  ${Typography.Body3}
  align-items: center;
  cursor: pointer;
  display: flex;
  padding: 0.375rem 0.75rem;

  &:hover {
    background-color: ${Colors.Semantic.Neutral.light_grey};
  }
`

export const ColumnSelect = () => {
  const { columnDefs, gridApi, listing } = useManifestContext()
  const { formatMessage } = useIntl()
  const { onManifestColumnsChanged, onManifestColumnsReset } =
    useManifestTableTracking(listing)
  const [buttonElement, setButtonElement] = useState<HTMLButtonElement | null>(
    null
  )
  const { setFalse: close, toggle, value: open } = useBoolean(false)
  const [, forceRender] = useReducer((n: number) => n + 1, 0)

  const resetColumns = useCallback(() => {
    if (!gridApi) return
    onManifestColumnsReset()
    for (const col of columnDefs) {
      if (!col.field || col.lockPinned) continue
      gridApi.setColumnsVisible([col.field], !col.hide)
    }
    forceRender()
  }, [columnDefs, gridApi, onManifestColumnsReset])

  const toggleColumn = useCallback(
    (colId: string, currentlyVisible: boolean) => {
      if (!gridApi) return
      gridApi.setColumnsVisible([colId], !currentlyVisible)
      onManifestColumnsChanged(colId, !currentlyVisible)
      forceRender()
    },
    [gridApi, onManifestColumnsChanged]
  )

  const allColStates = gridApi?.getColumnState()
  const columns = allColStates
    ? columnDefs.reduce<
        {
          configuredVisible: boolean
          disabled: boolean
          field: string
          headerName: string
          visible: boolean
        }[]
      >((acc, col) => {
        if (!col.field) return acc
        const colState = allColStates.find((s) => s.colId === col.field)
        acc.push({
          configuredVisible: !col.hide,
          disabled: !!col.lockPinned,
          field: col.field,
          headerName: col.headerName ?? col.field,
          visible: !colState?.hide,
        })
        return acc
      }, [])
    : []

  const canReset = columns.some((c) => c.visible !== c.configuredVisible)

  return (
    <>
      <ControlButton
        appearance={open ? 'primary' : 'secondary'}
        onClick={toggle}
        ref={setButtonElement}
        size={ButtonSizes.small}
        title={formatMessage({
          id: 'Listing.manifestAgGrid.searchBar.columns',
        })}
      >
        <FontAwesomeIcon icon={faGrid2} />
        <ControlButtonLabel>
          <FormattedMessage id="Listing.manifestAgGrid.searchBar.columns" />
        </ControlButtonLabel>
      </ControlButton>
      {open && (
        <Popover
          referenceElement={buttonElement}
          setOpen={(v: boolean) => {
            if (!v) close()
          }}
          trapFocus
        >
          <Panel>
            <Heading>
              <Title>
                <FormattedMessage id="Listing.manifestAgGrid.columnSelect.heading" />
              </Title>
              {canReset && (
                <ResetButton
                  onClick={resetColumns}
                  title={formatMessage({
                    id: 'Listing.manifestAgGrid.columnSelect.reset',
                  })}
                  type="button"
                >
                  <FontAwesomeIcon icon={faArrowRotateLeft} />
                </ResetButton>
              )}
            </Heading>
            <Group
              aria-label={formatMessage({
                id: 'Listing.manifestAgGrid.columnSelect.groupLabel',
              })}
              role="group"
            >
              {columns.map(({ disabled, field, headerName, visible }) => (
                <Item key={field}>
                  <Checkbox
                    checked={visible}
                    disabled={disabled}
                    label={headerName}
                    onChange={() => toggleColumn(field, visible)}
                  />
                </Item>
              ))}
            </Group>
          </Panel>
        </Popover>
      )}
    </>
  )
}
